import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import AccessRequestsWidget from "@/components/AccessRequestsWidget";
import {
  Users,
  Wrench,
  ShoppingCart,
  BarChart3,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Eye,
  UserCog,
  CheckCircle,
  Clock,
  AlertTriangle,
  Package,
  Briefcase,
  Droplet,
  FolderKanban,
  Truck,
  Send,
  DollarSign,
  Building2,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  RefreshCw,
  Mail,
  UserPlus,
  Check,
  X,
  Save,
  Settings,
  Calendar,
  Shield,
  MessageSquare,
  MessageCircle,
  FileText,
  ExternalLink,
  ClipboardList,
  Layers,
  Play,
  Archive,
  Star,
  Menu,
  User,
  History,
  LogOut,
  Droplets,
  Link2,
  MapPin,
  StickyNote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import ProjectManagement from "@/pages/ProjectManagementPage";
import AssetRegistry from "@/components/admin/AssetRegistry";
import MaintenanceManagement from "@/components/admin/MaintenanceManagement";
import AdminWorkOrdersBoard from "@/components/admin/AdminWorkOrdersBoard";
import BackordersManagement from "@/components/admin/BackordersManagement";
import ContractorManagement from "@/components/admin/ContractorManagement";
import BillingManagement from "@/components/admin/BillingManagement";
import SecurityDashboard from "@/components/admin/SecurityDashboard";
import UserManagement from "@/components/admin/UserManagement";
import OrdersManagement from "@/components/admin/OrdersManagement";
import PMSchedulesManagement from "@/components/admin/PMSchedulesManagement";
import HospitalManagement from "@/components/admin/HospitalManagement";
import SupplierManagement from "@/components/admin/SupplierManagement";
import AuditLogDashboard from "@/components/admin/AuditLogDashboard";
import AnalyticsDashboard from "@/components/admin/AnalyticsDashboard";
import BulkEmailModal from "@/components/admin/BulkEmailModal";
import EmailCampaigns from "@/components/admin/EmailCampaigns";
import AdminGlovesPricing from "@/pages/AdminGlovesPricing";
import AdminAnsellDatasheets from "@/pages/AdminAnsellDatasheets";
import QuickNotes from "@/components/admin/QuickNotes";
import AdminEnvironmentalTesting from "@/components/admin/AdminEnvironmentalTesting";
import QuickBooksIntegration from "@/components/admin/QuickBooksIntegration";
import CourierJobsManagement from "@/components/admin/CourierJobsManagement";
import RegistrationLeads from "@/components/admin/RegistrationLeads";
import ExecutiveSummaryGenerator from "@/components/admin/ExecutiveSummaryGenerator";
import DualRoleManagement from "@/components/admin/DualRoleManagement";
import TelegramConnect from "@/components/TelegramConnect";
import TelegramInviteBanner from "@/components/TelegramInviteBanner";
import CampaignAlertsPanel from "@/components/CampaignAlertsPanel";
import { EmptyState } from "@/components/ui/empty-state";
import { formatLabel, cleanDisplayName, formatStatus, formatRequestType, formatDate } from "@/utils/formatters";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { SkeletonStatsGrid, SkeletonList, SkeletonDashboard } from "@/components/ui/skeleton-loaders";

// Stats Card Component - Enhanced with better shadows and hover
const StatsCard = ({ title, value, icon: Icon, color, link, subtitle }) => {
  const colorClasses = {
    blue: "bg-gradient-to-br from-blue-50 to-blue-100/50 text-blue-700 border-blue-200",
    amber: "bg-gradient-to-br from-amber-50 to-amber-100/50 text-amber-700 border-amber-200",
    green: "bg-gradient-to-br from-emerald-50 to-emerald-100/50 text-emerald-700 border-emerald-200",
    purple: "bg-gradient-to-br from-violet-50 to-violet-100/50 text-violet-700 border-violet-200",
    red: "bg-gradient-to-br from-red-50 to-red-100/50 text-red-700 border-red-200"
  };
  
  return (
    <Link 
      to={link || "#"}
      className="stats-card group block"
      data-testid={`stats-card-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-slate-500 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-slate-900 mt-2 tracking-tight">{value}</p>
          {subtitle && (
            <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center border ${colorClasses[color] || colorClasses.blue} group-hover:scale-105 transition-transform`}>
          <Icon className="w-7 h-7" strokeWidth={1.5} />
        </div>
      </div>
    </Link>
  );
};

// Status Badge Component - Enhanced with formatters
const StatusBadge = ({ status }) => {
  const { label, color } = formatStatus(status);

  return (
    <span className={`badge-status ${color}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
      {label}
    </span>
  );
};

// Request Type Badge - NEW
const RequestTypeBadge = ({ type }) => {
  const { label, color } = formatRequestType(type);
  
  return (
    <span className={`text-sm font-medium ${color}`}>
      {label}
    </span>
  );
};

// Integration Status Card
const IntegrationStatusCard = ({ token }) => {
  const [systems, setSystems] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIntegrationStatus = async () => {
      try {
        // Fetch supported systems
        const systemsRes = await fetch(`${API}/integrations/supported-systems`);
        if (systemsRes.ok) {
          const data = await systemsRes.json();
          setSystems(data.systems || []);
        }
        
        // Fetch active integrations (if any)
        if (token) {
          const integrationsRes = await fetch(`${API}/integrations`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (integrationsRes.ok) {
            const data = await integrationsRes.json();
            setIntegrations(data.integrations || []);
          }
        }
      } catch (error) {
        console.error("Failed to fetch integration status:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchIntegrationStatus();
  }, [token]);

  const statusColors = {
    active: { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500" },
    inactive: { bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400" },
    error: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" },
    pending_setup: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" }
  };

  return (
    <div className="card-premium-elevated p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Layers className="w-4 h-4 text-indigo-700" strokeWidth={2} />
          </div>
          Integration Status
        </h3>
        <Link to="/developer-hub" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 hover:gap-2 transition-all">
          Developer Hub <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Active Integrations */}
          {integrations.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Connected ERPs</p>
              {integrations.map((integration) => {
                const status = statusColors[integration.status] || statusColors.inactive;
                return (
                  <div key={integration.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${status.dot}`} />
                      <div>
                        <p className="text-sm font-medium text-slate-900">{integration.name}</p>
                        <p className="text-xs text-slate-500">{integration.adapter_type}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.bg} ${status.text}`}>
                      {integration.status?.replace('_', ' ')}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-600 mb-1">No ERPs Connected Yet</p>
              <p className="text-xs text-slate-500">Integration-ready for Oracle, Epic, NetSuite, SAP</p>
            </div>
          )}
          
          {/* Supported Systems Summary */}
          <div className="pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                <span className="font-semibold text-indigo-600">{systems.length}</span> ERP systems supported
              </p>
              <div className="flex -space-x-2">
                {systems.slice(0, 4).map((system, idx) => (
                  <div 
                    key={system.id} 
                    className="w-7 h-7 bg-white border-2 border-white rounded-full flex items-center justify-center shadow-sm"
                    title={system.name}
                  >
                    <span className="text-[10px] font-bold text-slate-600">
                      {system.name.charAt(0)}
                    </span>
                  </div>
                ))}
                {systems.length > 4 && (
                  <div className="w-7 h-7 bg-slate-200 border-2 border-white rounded-full flex items-center justify-center shadow-sm">
                    <span className="text-[10px] font-bold text-slate-600">+{systems.length - 4}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  if (loading) {
    return <SkeletonDashboard />;
  }

  const maintenanceTotal = Object.values(stats?.maintenance_stats || {}).reduce((a, b) => a + b, 0);
  const ordersTotal = Object.values(stats?.order_stats || {}).reduce((a, b) => a + b, 0);
  const usersTotal = Object.values(stats?.user_stats || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-8">
      {/* Telegram Invite Banner for Admins */}
      <TelegramInviteBanner 
        token={token} 
        userType="admin" 
        userName={user?.name?.split(' ')[0]}
      />
      
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title">Dashboard Overview</h2>
          <p className="page-subtitle">Monitor your facility management at a glance</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid-responsive-cards">
        <StatsCard
          title="Maintenance Requests"
          value={maintenanceTotal}
          icon={Wrench}
          color="blue"
          link="/admin/maintenance"
          subtitle="Total active requests"
        />
        <StatsCard
          title="Pending"
          value={stats?.maintenance_stats?.pending || 0}
          icon={Clock}
          color="amber"
          link="/admin/maintenance"
          subtitle="Awaiting action"
        />
        <StatsCard
          title="Order Requests"
          value={ordersTotal}
          icon={ShoppingCart}
          color="green"
          link="/admin/orders"
          subtitle="Procurement requests"
        />
        <StatsCard
          title="Staff Members"
          value={usersTotal}
          icon={Users}
          color="purple"
          link="/admin/users"
          subtitle="Active team members"
        />
      </div>

      {/* Integration Status Card - Only for super_admin */}
      {user?.role === "super_admin" && (
        <IntegrationStatusCard token={token} />
      )}

      {/* Access Requests Widget - Only for super_admin */}
      {user?.role === "super_admin" && (
        <AccessRequestsWidget />
      )}

      {/* Supplier Campaign Alerts - For all admins */}
      <CampaignAlertsPanel 
        token={token} 
        userType="admin"
        maxAlerts={5}
        onAlertClick={(alert) => {
          toast.info(`Viewing campaign from ${alert.supplier_name}: ${alert.product_name}`);
        }}
      />

      {/* Recent Activity Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent Maintenance */}
        <div className="card-premium-elevated p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Wrench className="w-4 h-4 text-blue-700" strokeWidth={2} />
              </div>
              Recent Maintenance
            </h3>
            <Link to="/admin/maintenance" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 hover:gap-2 transition-all">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-3">
            {stats?.recent_maintenance?.length > 0 ? (
              stats.recent_maintenance.map((req) => (
                <Link 
                  key={req.id} 
                  to="/admin/maintenance" 
                  className="flex items-center justify-between p-4 bg-slate-50/80 rounded-xl hover:bg-slate-100 transition-all hover:shadow-sm group"
                  data-testid={`maintenance-item-${req.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-slate-900 font-medium group-hover:text-blue-700 transition-colors">
                      <RequestTypeBadge type={req.request_type_label || req.request_type} />
                    </p>
                    <p className="text-slate-500 text-sm mt-0.5 truncate">
                      {cleanDisplayName(req.data?.hospital_name || req.hospital_name || 'Unknown')}
                    </p>
                  </div>
                  <StatusBadge status={req.status} />
                </Link>
              ))
            ) : (
              <EmptyState 
                icon="maintenance"
                title="No recent requests"
                description="New maintenance requests will appear here"
                compact
              />
            )}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="card-premium-elevated p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-4 h-4 text-emerald-700" strokeWidth={2} />
              </div>
              Recent Orders
            </h3>
            <Link to="/admin/orders" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 hover:gap-2 transition-all">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-3">
            {stats?.recent_orders?.length > 0 ? (
              stats.recent_orders.map((req) => (
                <Link 
                  key={req.id} 
                  to="/admin/orders" 
                  className="flex items-center justify-between p-4 bg-slate-50/80 rounded-xl hover:bg-slate-100 transition-all hover:shadow-sm group"
                  data-testid={`order-item-${req.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-slate-900 font-medium capitalize group-hover:text-blue-700 transition-colors">
                      {formatLabel(req.order_type)}
                    </p>
                    <p className="text-slate-500 text-sm mt-0.5 truncate">
                      {cleanDisplayName(req.data?.hospital_name || 'Unknown')}
                    </p>
                  </div>
                  <StatusBadge status={req.status} />
                </Link>
              ))
            ) : (
              <EmptyState 
                icon="packages"
                title="No recent orders"
                description="New order requests will appear here"
                compact
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// UserManagement component has been extracted to /app/frontend/src/components/admin/UserManagement.jsx

// MaintenanceManagement component has been extracted to /app/frontend/src/components/admin/MaintenanceManagement.jsx

// OrdersManagement component has been extracted to /app/frontend/src/components/admin/OrdersManagement.jsx

// =====================
// AUTO-SEND SETTINGS COMPONENT
// =====================

const AutoSendSettings = ({ token }) => {
  const [settings, setSettings] = useState({
    enabled: false,
    max_margin_percentage: 35.0,
    min_available_quantity_percentage: 100.0,
    max_lead_time_days: 14,
    require_all_items_quoted: true,
    auto_select_best_price: true,
    notification_email: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/backorders/settings/auto-send`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (err) {
      console.error("Failed to fetch settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/backorders/settings/auto-send`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        toast.success("Auto-send settings saved");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to save settings");
      }
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-slate-900 font-manrope">Auto-Send Thresholds</h3>
          <p className="text-slate-500 text-sm">Configure when quotes are automatically sent to hospitals without manual review</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${settings.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
            {settings.enabled ? 'Enabled' : 'Disabled'}
          </span>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
          />
        </div>
      </div>

      <div className={`space-y-6 ${!settings.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
        {/* Margin Threshold */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="text-slate-700">Maximum Margin Percentage</Label>
            <p className="text-slate-400 text-xs mb-2">Quotes with margin above this require manual review</p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={settings.max_margin_percentage}
                onChange={(e) => setSettings({ ...settings, max_margin_percentage: parseFloat(e.target.value) || 0 })}
                className="w-24 border-slate-200"
                step="0.5"
                min="0"
                max="100"
              />
              <span className="text-slate-500">%</span>
            </div>
          </div>

          <div>
            <Label className="text-slate-700">Maximum Lead Time</Label>
            <p className="text-slate-400 text-xs mb-2">Quotes with longer lead time require manual review</p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={settings.max_lead_time_days}
                onChange={(e) => setSettings({ ...settings, max_lead_time_days: parseInt(e.target.value) || 0 })}
                className="w-24 border-slate-200"
                min="1"
                max="365"
              />
              <span className="text-slate-500">days</span>
            </div>
          </div>
        </div>

        {/* Quantity Threshold */}
        <div>
          <Label className="text-slate-700">Minimum Available Quantity</Label>
          <p className="text-slate-400 text-xs mb-2">Supplier must have at least this % of requested quantity available</p>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={settings.min_available_quantity_percentage}
              onChange={(e) => setSettings({ ...settings, min_available_quantity_percentage: parseFloat(e.target.value) || 0 })}
              className="w-24 border-slate-200"
              step="5"
              min="0"
              max="100"
            />
            <span className="text-slate-500">%</span>
          </div>
        </div>

        {/* Boolean Options */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div>
              <Label className="text-slate-900">Require All Items Quoted</Label>
              <p className="text-slate-400 text-xs">All items must have at least one quote</p>
            </div>
            <Switch
              checked={settings.require_all_items_quoted}
              onCheckedChange={(checked) => setSettings({ ...settings, require_all_items_quoted: checked })}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div>
              <Label className="text-slate-900">Auto-Select Best Price</Label>
              <p className="text-slate-400 text-xs">Automatically choose lowest price per item</p>
            </div>
            <Switch
              checked={settings.auto_select_best_price}
              onCheckedChange={(checked) => setSettings({ ...settings, auto_select_best_price: checked })}
            />
          </div>
        </div>

        {/* Notification Email */}
        <div>
          <Label className="text-slate-700">Notification Email (Optional)</Label>
          <p className="text-slate-400 text-xs mb-2">Receive notifications when quotes are auto-sent</p>
          <Input
            type="email"
            value={settings.notification_email || ""}
            onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
            placeholder="admin@sterilemate.com"
            className="max-w-md border-slate-200"
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end mt-6 pt-6 border-t border-slate-200">
        <Button onClick={handleSave} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>
    </div>
  );
};

// =====================
// BACKORDERS MANAGEMENT
// =====================

const BACKORDER_STATUS_CONFIG = {
  submitted: { label: "Submitted", color: "bg-blue-100 text-blue-700 border-blue-200" },
  sent_to_suppliers: { label: "Sent to Suppliers", color: "bg-purple-100 text-purple-700 border-purple-200" },
  quotes_received: { label: "Quotes Received", color: "bg-amber-100 text-amber-700 border-amber-200" },
  priced: { label: "Priced", color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  pending_approval: { label: "Pending Approval", color: "bg-orange-100 text-orange-700 border-orange-200" },
  approved: { label: "Approved", color: "bg-green-100 text-green-700 border-green-200" },
  ordered: { label: "Ordered", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  shipped: { label: "Shipped", color: "bg-teal-100 text-teal-700 border-teal-200" },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700 border-red-200" }
};

// BackordersManagement component has been extracted to /app/frontend/src/components/admin/BackordersManagement.jsx


// Contractor Management Panel

// ContractorManagement component has been extracted to /app/frontend/src/components/admin/ContractorManagement.jsx

// PMSchedulesManagement component has been extracted to /app/frontend/src/components/admin/PMSchedulesManagement.jsx

// Security Dashboard Component

// SecurityDashboard component has been extracted to /app/frontend/src/components/admin/SecurityDashboard.jsx

const LettersManagement = () => {
  const { token } = useAuth();
  const [copiedId, setCopiedId] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const letters = [
    {
      id: "hospital",
      title: "Hospital Partner Welcome Letter",
      description: "Introduction letter for hospital partners explaining AskIsaac platform benefits",
      audience: "Hospital Partners",
      url: "/welcome",
      color: "from-cyan-600 to-blue-600",
      bgColor: "bg-cyan-50",
      borderColor: "border-cyan-200"
    },
    {
      id: "supplier",
      title: "Supplier Partner Welcome Letter",
      description: "Introduction letter for supplier partners explaining the quote and RFQ system",
      audience: "Supplier Partners",
      url: "/welcome/supplier",
      color: "from-purple-600 to-indigo-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200"
    },
    {
      id: "contractor",
      title: "Contractor Partner Welcome Letter",
      description: "Introduction letter for contractor partners explaining job dispatch and portal features",
      audience: "Contractor Partners",
      url: "/welcome/contractor",
      color: "from-amber-500 to-orange-500",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200"
    }
  ];

  const copyEmailHtml = async (templateId) => {
    try {
      const response = await fetch(`${API}/api/email-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.html) {
        await navigator.clipboard.writeText(data.html);
        setCopiedId(templateId);
        toast.success("Email HTML copied to clipboard!");
        setTimeout(() => setCopiedId(null), 2000);
      }
    } catch (error) {
      toast.error("Failed to copy email template");
    }
  };

  const downloadEmailHtml = async (templateId, templateName) => {
    try {
      const response = await fetch(`${API}/api/email-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.html) {
        const blob = new Blob([data.html], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${templateId}_welcome_email.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Email template downloaded!");
      }
    } catch (error) {
      toast.error("Failed to download email template");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 font-manrope">Letters & Templates</h2>
          <p className="text-slate-500 text-sm">Welcome letters and email templates for partners</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {letters.map((letter) => (
          <div 
            key={letter.id} 
            className={`${letter.bgColor} ${letter.borderColor} border rounded-xl overflow-hidden hover:shadow-lg transition-shadow`}
          >
            {/* Header gradient */}
            <div className={`bg-gradient-to-r ${letter.color} px-6 py-4`}>
              <FileText className="w-8 h-8 text-white mb-2" />
              <h3 className="text-lg font-semibold text-white">{letter.title}</h3>
              <span className="text-xs text-white/80 font-medium">{letter.audience}</span>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <p className="text-slate-600 text-sm mb-4">{letter.description}</p>
              
              {/* Web Letter Actions */}
              <div className="flex gap-2 mb-3">
                <a
                  href={letter.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  View Web
                </a>
                <a
                  href={letter.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              
              {/* Email Template Actions */}
              <div className="border-t border-slate-200 pt-3 mt-3">
                <p className="text-xs text-slate-500 mb-2 font-medium">Email Template (Outlook/Mailchimp)</p>
                <div className="flex gap-2 mb-2">
                  <a
                    href={`${API}/api/email-templates/${letter.id}/preview`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Preview for Outlook
                  </a>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyEmailHtml(letter.id)}
                    className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      copiedId === letter.id 
                        ? "bg-emerald-100 text-emerald-700 border border-emerald-200" 
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    {copiedId === letter.id ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Copy HTML
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => downloadEmailHtml(letter.id, letter.title)}
                    className="inline-flex items-center justify-center px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors"
                    title="Download HTML file"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Instructions Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-8">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" />
          Using Email Templates
        </h3>
        <div className="space-y-4 text-sm text-slate-700">
          <div>
            <p className="font-semibold text-slate-900 mb-2">📧 For Outlook:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Click <strong>&quot;Preview for Outlook&quot;</strong> - opens email in new tab</li>
              <li>Press <strong>Ctrl+A</strong> (Select All) then <strong>Ctrl+C</strong> (Copy)</li>
              <li>Go to Outlook, create new email, and <strong>Ctrl+V</strong> (Paste)</li>
              <li>The formatting will be preserved!</li>
            </ol>
          </div>
          <div>
            <p className="font-semibold text-slate-900 mb-2">📨 For Mailchimp:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Click <strong>&quot;Copy HTML&quot;</strong> on the template</li>
              <li>In Mailchimp, create campaign → <strong>&quot;Code your own&quot;</strong></li>
              <li>Paste the HTML directly into the editor</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Web URLs Section */}
      <div className="bg-slate-100 rounded-xl p-6">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <ExternalLink className="w-5 h-5 text-slate-600" />
          Direct Web Links
        </h3>
        <p className="text-slate-600 text-sm mb-4">
          Share these URLs directly with partners to view the letters online:
        </p>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-3">
            <span className="font-medium text-slate-700 w-24">Hospitals:</span>
            <code className="bg-white px-3 py-1 rounded border border-slate-200 text-slate-600 flex-1">
              {window.location.origin}/welcome
            </code>
          </li>
          <li className="flex items-center gap-3">
            <span className="font-medium text-slate-700 w-24">Suppliers:</span>
            <code className="bg-white px-3 py-1 rounded border border-slate-200 text-slate-600 flex-1">
              {window.location.origin}/welcome/supplier
            </code>
          </li>
          <li className="flex items-center gap-3">
            <span className="font-medium text-slate-700 w-24">Contractors:</span>
            <code className="bg-white px-3 py-1 rounded border border-slate-200 text-slate-600 flex-1">
              {window.location.origin}/welcome/contractor
            </code>
          </li>
        </ul>
      </div>
    </div>
  );
};

// Main Admin Dashboard
export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [demoEntities, setDemoEntities] = useState({ hospitals: [], contractors: [], suppliers: [] });
  const [demoMode, setDemoMode] = useState(null); // { type, name, token }
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchDemoEntities = async () => {
    try {
      const res = await fetch(`${API}/admin/demo-entities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDemoEntities(data);
      }
    } catch (err) {
      console.error("Failed to fetch demo entities:", err);
    }
  };

  const startDemoMode = async (type, id, name) => {
    try {
      setLoadingDemo(true);
      const res = await fetch(`${API}/admin/view-as/${type}/${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDemoMode({
          type: data.viewing_as,
          name: data.entity_name,
          token: data.demo_token
        });
        setShowDemoModal(false);
        toast.success(`Demo Mode: Now viewing as ${data.entity_name}`);
        
        // Open the appropriate portal in a new tab
        if (type === "hospital") {
          localStorage.setItem("hospital_token", data.demo_token);
          window.open("/hospital/dashboard", "_blank");
        } else if (type === "contractor") {
          localStorage.setItem("contractor_token", data.demo_token);
          window.open("/contractor/dashboard", "_blank");
        } else if (type === "supplier") {
          localStorage.setItem("supplier_token", data.demo_token);
          window.open("/supplier/dashboard", "_blank");
        }
      } else {
        toast.error("Failed to start demo mode");
      }
    } catch (err) {
      toast.error("Failed to start demo mode");
    } finally {
      setLoadingDemo(false);
    }
  };

  const exitDemoMode = () => {
    setDemoMode(null);
    localStorage.removeItem("hospital_token");
    localStorage.removeItem("contractor_token");
    localStorage.removeItem("supplier_token");
    toast.success("Demo mode ended");
  };
  
  const navItems = [
    { path: "/admin", label: "Overview", icon: BarChart3 },
    { path: "/admin/projects", label: "Projects", icon: FolderKanban },
    { path: "/admin/assets", label: "Assets", icon: Package },
    { path: "/admin/backorders", label: "Sourcing Desk", icon: Truck },
    { path: "/admin/courier-jobs", label: "Courier Jobs", icon: Truck },
    { path: "/fleet", label: "Fleet Tracking", icon: MapPin, external: true },
    { path: "/admin/contractors", label: "Contractors", icon: Briefcase },
    { path: "/admin/hospitals", label: "Hospitals", icon: Building2 },
    { path: "/admin/suppliers", label: "Suppliers", icon: Package },
    { path: "/admin/registration-leads", label: "Registration Leads", icon: UserPlus },
    { path: "/admin/billing", label: "Billing", icon: DollarSign },
    { path: "/admin/users", label: "Users", icon: Users },
    { path: "/admin/dual-role", label: "Dual-Role", icon: Shield, superAdminOnly: true },
    { path: "/admin/maintenance", label: "Maintenance", icon: Wrench },
    { path: "/admin/orders", label: "Orders", icon: ShoppingCart },
    { path: "/admin/pm-schedules", label: "PM Schedules", icon: Calendar },
    { path: "/admin/email-campaigns", label: "Email Campaigns", icon: Mail },
    { path: "/admin/quick-notes", label: "Quick Notes", icon: StickyNote },
    { path: "/admin/executive-summary", label: "Executive Summary", icon: FileText },
    { path: "/admin/environmental-testing", label: "Water Testing", icon: Droplets },
    { path: "/admin/quickbooks", label: "QuickBooks", icon: Link2 },
    { path: "/admin/analytics", label: "Analytics", icon: BarChart3 },
    { path: "/admin/letters", label: "Letters", icon: FileText },
    { path: "/admin/audit-log", label: "Audit Log", icon: History },
    { path: "/admin/security", label: "Security", icon: Shield },
    // Sales & Training Tools
    { path: "/sales-playbook", label: "Sales Playbook", icon: ClipboardList, divider: "Sales Tools" },
    { path: "/supply-chain-playbook", label: "Supply Chain Playbook", icon: Package },
    { path: "/compliance-demo", label: "Compliance Demo", icon: Shield },
    { path: "/onboarding-playbook", label: "Onboarding Playbook", icon: UserPlus },
    // Developer Tools
    { path: "/developer-hub", label: "Developer Hub", icon: Layers, divider: "Developer" },
  ];

  // Filter nav items based on permissions
  const filteredNav = navItems.filter(item => {
    if (user.role === "super_admin") return true;
    // Items marked superAdminOnly are only for super admins
    if (item.superAdminOnly) return false;
    if (item.path === "/admin") return true;
    if (item.path === "/admin/projects" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/assets" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/backorders" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/courier-jobs" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/contractors" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/hospitals" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/suppliers" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/registration-leads" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/billing" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/users" && user.permissions?.includes("full")) return true;
    if (item.path === "/admin/maintenance" && (user.permissions?.includes("full") || user.permissions?.includes("maintenance_only") || user.permissions?.includes("view_only"))) return true;
    if (item.path === "/admin/orders" && (user.permissions?.includes("full") || user.permissions?.includes("orders_only") || user.permissions?.includes("view_only"))) return true;
    if (item.path === "/admin/pm-schedules" && (user.permissions?.includes("full") || user.permissions?.includes("maintenance_only") || user.permissions?.includes("view_only"))) return true;
    if (item.path === "/admin/email-campaigns" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/environmental-testing" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/quickbooks" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/analytics" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/letters" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/audit-log" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/admin/security" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    // Sales Tools - accessible to super_admin and hub_admin
    if (item.path === "/sales-playbook" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/supply-chain-playbook" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    if (item.path === "/onboarding-playbook" && (user.permissions?.includes("full") || user.role === "hub_admin")) return true;
    // Developer Tools - accessible to super_admin
    if (item.path === "/developer-hub" && user.role === "super_admin") return true;
    return false;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      {/* Mobile Navigation Drawer */}
      {mobileMenuOpen && (
        <>
          <div 
            className="mobile-nav-overlay" 
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="mobile-nav-drawer">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h1 className="text-slate-900 font-bold">Admin Portal</h1>
                    <p className="text-slate-400 text-xs">AskIsaac</p>
                  </div>
                </div>
                <button 
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            <nav className="p-4 space-y-1 overflow-y-auto" style={{maxHeight: 'calc(100vh - 180px)'}}>
              {filteredNav.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    location.pathname === item.path
                      ? "bg-blue-50 text-blue-700 border border-blue-200"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role?.replace(/_/g, ' ')}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  logout();
                  navigate("/login");
                  toast.success("Logged out successfully");
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </>
      )}

      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Mobile Menu Toggle - Now visible on all screens */}
              <button 
                onClick={() => setMobileMenuOpen(true)}
                className="p-2 -ml-2 hover:bg-slate-100 rounded-lg"
                data-testid="mobile-menu-toggle"
              >
                <Menu className="w-6 h-6 text-slate-600" />
              </button>
              
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-slate-900 font-bold text-lg font-manrope">Admin Portal</h1>
                  <p className="text-slate-400 text-xs">AskIsaac | </p>
                </div>
              </Link>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Theme Toggle */}
              <ThemeToggle variant="ghost" size="icon" />
              
              {/* Telegram Connect */}
              <TelegramConnect token={token} userType="admin" />
              
              {/* Seed Data Button - Super Admin Only */}
              {user?.role === "super_admin" && (
                <>
                  <Button
                    onClick={async () => {
                      try {
                        toast.loading("Seeding Harris Health data...");
                        const res = await fetch(`${API}/admin/seed-harris-health`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.dismiss();
                        if (res.ok) {
                          const data = await res.json();
                          toast.success(data.message);
                        } else {
                          const err = await res.json();
                          toast.error(err.detail || "Failed to seed data");
                        }
                      } catch (err) {
                        toast.dismiss();
                        toast.error("Failed to seed Harris Health data");
                      }
                    }}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="seed-harris-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Seed Data
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        toast.loading("Activating demo accounts...");
                        const res = await fetch(`${API}/admin/activate-demo-accounts`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.dismiss();
                        if (res.ok) {
                          const data = await res.json();
                          toast.success(
                            <div>
                              <p className="font-semibold">Demo accounts activated!</p>
                              <p className="text-xs mt-1">Contractor: demo@contractor.sterilemate.com</p>
                              <p className="text-xs">Supplier: demo@supplier.sterilemate.com</p>
                            </div>
                          );
                        } else {
                          const err = await res.json();
                          toast.error(err.detail || "Failed to activate demo accounts");
                        }
                      } catch (err) {
                        toast.dismiss();
                        toast.error("Failed to activate demo accounts");
                      }
                    }}
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="seed-demo-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Seed Demo</span>
                  </Button>
                  <Button
                    onClick={() => { setShowDemoModal(true); fetchDemoEntities(); }}
                    variant="outline"
                    size="sm"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="demo-mode-btn"
                  >
                    <Eye className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">View As</span>
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        toast.loading("Setting up Telegram webhook...");
                        const res = await fetch(`${API}/admin/telegram/setup-webhook`, {
                          method: "POST",
                          headers: { 
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json"
                          },
                          body: JSON.stringify({
                            webhook_url: "https://sterilemate.com/api/telegram/webhook"
                          })
                        });
                        toast.dismiss();
                        if (res.ok) {
                          const data = await res.json();
                          if (data.ok) {
                            toast.success(
                              <div>
                                <p className="font-semibold">Telegram Webhook Active!</p>
                                <p className="text-xs mt-1">Bot will now receive messages</p>
                              </div>
                            );
                          } else {
                            toast.error(data.description || "Webhook setup failed");
                          }
                        } else {
                          const err = await res.json();
                          toast.error(err.detail || "Failed to setup webhook");
                        }
                      } catch (err) {
                        toast.dismiss();
                        toast.error("Failed to setup Telegram webhook");
                      }
                    }}
                    size="sm"
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    data-testid="telegram-webhook-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Setup Telegram</span>
                  </Button>
                </>
              )}
              
              {/* Demo Mode Active Banner */}
              {demoMode && (
                <div className="flex items-center gap-2 px-2 sm:px-3 py-1.5 bg-purple-100 border border-purple-300 rounded-lg">
                  <Eye className="w-4 h-4 text-purple-600" />
                  <span className="text-xs sm:text-sm text-purple-800 font-medium hidden sm:inline">Demo: {demoMode.name}</span>
                  <button onClick={exitDemoMode} className="text-purple-600 hover:text-purple-800">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {/* Bulk Email Button - Super Admin Only */}
              {user?.role === "super_admin" && (
                <Button
                  onClick={() => setShowBulkEmailModal(true)}
                  variant="outline"
                  size="sm"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="bulk-email-btn"
                >
                  <Mail className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Send Email</span>
                </Button>
              )}
              
              {/* Settings Icon */}
              <Link 
                to="/account/settings"
                className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                title="Account Settings"
                data-testid="admin-settings-link"
              >
                <Settings className="w-5 h-5" />
              </Link>

              {/* Profile Avatar Dropdown */}
              <div className="relative group">
                <Link 
                  to="/account/profile"
                  className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-all"
                  data-testid="admin-profile-link"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white text-sm font-bold overflow-hidden">
                    {user?.profile_image ? (
                      <img src={user.profile_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || "U"
                    )}
                  </div>
                </Link>
                {/* Dropdown on hover */}
                <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="p-3 border-b border-slate-100">
                    <p className="font-semibold text-slate-800 truncate">{user?.name || "User"}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <div className="p-1">
                    <Link to="/account/profile" className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">
                      <User className="w-4 h-4" />
                      My Profile
                    </Link>
                    <Link to="/account/settings" className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                  </div>
                  <div className="p-1 border-t border-slate-100">
                    <button
                      onClick={() => {
                        logout();
                        navigate("/login");
                        toast.success("Logged out successfully");
                      }}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg w-full text-left"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="text-right hidden md:block">
                <p className="text-slate-900 font-medium">{user?.name}</p>
                <p className="text-slate-500 text-sm capitalize">{user?.role?.replace(/_/g, ' ')}</p>
              </div>
              {/* Investor Portal Link - Super Admin Only */}
              {user?.role === "super_admin" && (
                <>
                  <Link
                    to="/investor"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-violet-100 text-violet-700 hover:bg-violet-200 transition-all"
                    data-testid="admin-investor-link"
                  >
                    <Briefcase className="w-4 h-4" />
                    <span className="hidden sm:inline">Investor Portal</span>
                  </Link>
                  <Link
                    to="/living-waters"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-cyan-100 text-cyan-700 hover:bg-cyan-200 transition-all"
                    data-testid="admin-living-waters-link"
                    title="Living Waters × Bleu Flux — Marketing Site"
                  >
                    <Droplet className="w-4 h-4" />
                    <span className="hidden sm:inline">Living Waters</span>
                  </Link>
                </>
              )}
              
              {/* Logout Button */}
              <button
                onClick={() => {
                  logout();
                  navigate("/login");
                  toast.success("Logged out successfully");
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 hover:text-red-700 transition-all"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs - Show on large screens as quick nav */}
      <div className="bg-white border-b border-slate-200 hidden xl:block">
        <div className="max-w-7xl mx-auto px-4 relative">
          {/* Scroll indicators - both sides */}
          <button 
            onClick={() => {
              const container = document.getElementById('admin-nav-tabs');
              if (container) container.scrollBy({ left: -200, behavior: 'smooth' });
            }}
            className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white via-white to-transparent z-10 flex items-center justify-start hover:from-slate-100 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-slate-400" />
          </button>
          <button 
            onClick={() => {
              const container = document.getElementById('admin-nav-tabs');
              if (container) container.scrollBy({ left: 200, behavior: 'smooth' });
            }}
            className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white via-white to-transparent z-10 flex items-center justify-end hover:from-slate-100 transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
          <div 
            id="admin-nav-tabs"
            className="flex gap-1 overflow-x-auto py-2 px-10 scroll-smooth"
            style={{scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent'}}
          >
            {filteredNav.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  location.pathname === item.path
                    ? 'bg-blue-700 text-white'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Routes>
          <Route path="/" element={<DashboardOverview />} />
          <Route path="/projects/*" element={<ProjectManagement />} />
          <Route path="/assets" element={<AssetRegistry />} />
          <Route path="/backorders/*" element={<BackordersManagement />} />
          <Route path="/courier-jobs/*" element={<CourierJobsManagement />} />
          <Route path="/contractors/*" element={<ContractorManagement />} />
          <Route path="/hospitals/*" element={<HospitalManagement />} />
          <Route path="/suppliers/*" element={<SupplierManagement />} />
          <Route path="/registration-leads" element={<RegistrationLeads />} />
          <Route path="/billing/*" element={<BillingManagement />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/dual-role" element={<DualRoleManagement token={token} />} />
          <Route path="/maintenance" element={<AdminWorkOrdersBoard />} />
          <Route path="/maintenance/legacy" element={<MaintenanceManagement />} />
          <Route path="/orders" element={<OrdersManagement />} />
          <Route path="/pm-schedules" element={<PMSchedulesManagement />} />
          <Route path="/email-campaigns" element={<EmailCampaigns />} />
          <Route path="/gloves-pricing" element={<AdminGlovesPricing />} />
          <Route path="/ansell-datasheets" element={<AdminAnsellDatasheets />} />
          <Route path="/quick-notes" element={<QuickNotes />} />
          <Route path="/executive-summary" element={<ExecutiveSummaryGenerator />} />
          <Route path="/environmental-testing" element={<AdminEnvironmentalTesting />} />
          <Route path="/quickbooks" element={<QuickBooksIntegration />} />
          <Route path="/analytics" element={<AnalyticsDashboard />} />
          <Route path="/letters" element={<LettersManagement />} />
          <Route path="/audit-log" element={<AuditLogDashboard />} />
          <Route path="/security" element={<SecurityDashboard />} />
        </Routes>
      </main>

      {/* Demo Mode Selection Modal */}
      <Dialog open={showDemoModal} onOpenChange={setShowDemoModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-600" />
              View As Different Role
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-4">
            Select an entity to view the platform from their perspective. A new tab will open with their view.
          </p>
          
          {/* Demo Accounts Section */}
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                  <Star className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-purple-900">Demo Accounts</h4>
                  <p className="text-xs text-purple-600">Pre-configured accounts for demonstrations</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API}/admin/setup-demo-accounts`, {
                        method: "POST",
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      if (res.ok) {
                        const data = await res.json();
                        toast.success(data.message);
                        fetchDemoEntities();
                      } else {
                        toast.error("Failed to setup demo accounts");
                      }
                    } catch (err) {
                      toast.error("Failed to setup demo accounts");
                    }
                  }}
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                  data-testid="setup-demo-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Setup Demo
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      toast.loading("Seeding Harris Health data...");
                      const res = await fetch(`${API}/admin/seed-harris-health`, {
                        method: "POST",
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.dismiss();
                      if (res.ok) {
                        const data = await res.json();
                        toast.success(data.message);
                        if (data.created?.length > 0) {
                          data.created.forEach(item => {
                            toast.info(`${item.name}: Setup link sent`);
                          });
                        }
                      } else {
                        const err = await res.json();
                        toast.error(err.detail || "Failed to seed data");
                      }
                    } catch (err) {
                      toast.dismiss();
                      toast.error("Failed to seed Harris Health data");
                    }
                  }}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                  data-testid="seed-harris-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Seed Harris Health
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              {/* Demo Hospital */}
              {demoEntities.hospitals?.find(h => h.is_demo) ? (
                <button
                  onClick={() => {
                    const demo = demoEntities.hospitals.find(h => h.is_demo);
                    startDemoMode("hospital", demo.id, demo.name);
                  }}
                  disabled={loadingDemo}
                  className="flex flex-col items-center p-3 bg-white hover:bg-blue-50 rounded-lg border-2 border-blue-300 hover:border-blue-500 transition-all"
                  data-testid="demo-hospital-btn"
                >
                  <Building2 className="w-6 h-6 text-blue-600 mb-1" />
                  <span className="text-xs font-medium text-blue-900">Hospital</span>
                  <span className="text-[10px] text-blue-500">Demo Memorial</span>
                </button>
              ) : (
                <div className="flex flex-col items-center p-3 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                  <Building2 className="w-6 h-6 text-slate-300 mb-1" />
                  <span className="text-xs text-slate-400">Hospital</span>
                  <span className="text-[10px] text-slate-300">Not setup</span>
                </div>
              )}
              
              {/* Demo Contractor */}
              {demoEntities.contractors?.find(c => c.is_demo) ? (
                <button
                  onClick={() => {
                    const demo = demoEntities.contractors.find(c => c.is_demo);
                    startDemoMode("contractor", demo.id, demo.name);
                  }}
                  disabled={loadingDemo}
                  className="flex flex-col items-center p-3 bg-white hover:bg-green-50 rounded-lg border-2 border-green-300 hover:border-green-500 transition-all"
                  data-testid="demo-contractor-btn"
                >
                  <Briefcase className="w-6 h-6 text-green-600 mb-1" />
                  <span className="text-xs font-medium text-green-900">Contractor</span>
                  <span className="text-[10px] text-green-500">Demo Pro</span>
                </button>
              ) : (
                <div className="flex flex-col items-center p-3 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                  <Briefcase className="w-6 h-6 text-slate-300 mb-1" />
                  <span className="text-xs text-slate-400">Contractor</span>
                  <span className="text-[10px] text-slate-300">Not setup</span>
                </div>
              )}
              
              {/* Demo Supplier */}
              {demoEntities.suppliers?.find(s => s.is_demo) ? (
                <button
                  onClick={() => {
                    const demo = demoEntities.suppliers.find(s => s.is_demo);
                    startDemoMode("supplier", demo.id, demo.name);
                  }}
                  disabled={loadingDemo}
                  className="flex flex-col items-center p-3 bg-white hover:bg-amber-50 rounded-lg border-2 border-amber-300 hover:border-amber-500 transition-all"
                  data-testid="demo-supplier-btn"
                >
                  <Truck className="w-6 h-6 text-amber-600 mb-1" />
                  <span className="text-xs font-medium text-amber-900">Supplier</span>
                  <span className="text-[10px] text-amber-500">Demo Medical</span>
                </button>
              ) : (
                <div className="flex flex-col items-center p-3 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                  <Truck className="w-6 h-6 text-slate-300 mb-1" />
                  <span className="text-xs text-slate-400">Supplier</span>
                  <span className="text-[10px] text-slate-300">Not setup</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Hospitals */}
            <div>
              <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Hospitals ({demoEntities.hospitals?.length || 0})
              </h4>
              <div className="grid gap-2 max-h-32 overflow-y-auto">
                {demoEntities.hospitals?.map(h => (
                  <button
                    key={h.id}
                    onClick={() => startDemoMode("hospital", h.id, h.name)}
                    disabled={loadingDemo}
                    className="text-left px-3 py-2 bg-slate-50 hover:bg-blue-50 rounded-lg border border-slate-200 hover:border-blue-300 transition-all"
                  >
                    <p className="font-medium text-sm text-slate-800">{h.name}</p>
                    <p className="text-xs text-slate-500">{h.email}</p>
                  </button>
                ))}
                {!demoEntities.hospitals?.length && <p className="text-sm text-slate-400">No hospitals registered</p>}
              </div>
            </div>

            {/* Contractors */}
            <div>
              <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Contractors ({demoEntities.contractors?.length || 0})
              </h4>
              <div className="grid gap-2 max-h-32 overflow-y-auto">
                {demoEntities.contractors?.map(c => (
                  <button
                    key={c.id}
                    onClick={() => startDemoMode("contractor", c.id, c.name)}
                    disabled={loadingDemo}
                    className="text-left px-3 py-2 bg-slate-50 hover:bg-green-50 rounded-lg border border-slate-200 hover:border-green-300 transition-all"
                  >
                    <p className="font-medium text-sm text-slate-800">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.email}</p>
                  </button>
                ))}
                {!demoEntities.contractors?.length && <p className="text-sm text-slate-400">No contractors registered</p>}
              </div>
            </div>

            {/* Suppliers */}
            <div>
              <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Truck className="w-4 h-4" /> Suppliers ({demoEntities.suppliers?.length || 0})
              </h4>
              <div className="grid gap-2 max-h-32 overflow-y-auto">
                {demoEntities.suppliers?.slice(0, 5).map(s => (
                  <button
                    key={s.id}
                    onClick={() => startDemoMode("supplier", s.id, s.name)}
                    disabled={loadingDemo}
                    className="text-left px-3 py-2 bg-slate-50 hover:bg-amber-50 rounded-lg border border-slate-200 hover:border-amber-300 transition-all"
                  >
                    <p className="font-medium text-sm text-slate-800">{s.name}</p>
                    <p className="text-xs text-slate-500">{s.email}</p>
                  </button>
                ))}
                {demoEntities.suppliers?.length > 5 && (
                  <p className="text-xs text-slate-400">+{demoEntities.suppliers.length - 5} more suppliers</p>
                )}
                {!demoEntities.suppliers?.length && <p className="text-sm text-slate-400">No suppliers registered</p>}
              </div>
            </div>
          </div>

          {loadingDemo && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              <span className="ml-2 text-sm text-slate-600">Loading demo view...</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Bulk Email Modal */}
      <BulkEmailModal
        isOpen={showBulkEmailModal}
        onClose={() => setShowBulkEmailModal(false)}
        token={token}
      />
    </div>
  );
}
